import { useQuery, useMutation } from "@tanstack/react-query";
import { Link } from "wouter";
import { PlatformLayout } from "./platform-layout";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ShieldAlert, RefreshCw, Loader2 } from "lucide-react";

interface HoneytokensSummary {
  totalWorkspaces: number;
  seededWorkspaces: number;
  unseededWorkspaces: number;
  totalLeadHoneytokens: number;
  totalClientHoneytokens: number;
  totalTouchEvents: number;
}

interface HoneytokenLeadRow {
  id: string;
  workspaceId: string;
  workspaceName: string | null;
  recordId: string | null;
  leadName: string | null;
  createdAt: string | null;
}

interface HoneytokenClientRow {
  id: string;
  workspaceId: string;
  workspaceName: string | null;
  recordId: string | null;
  accountName: string | null;
  createdAt: string | null;
}

interface TouchEventRow {
  id: string;
  workspaceId: string;
  workspaceName: string | null;
  recordType: "lead" | "client";
  recordId: string;
  recordName: string | null;
  action: string;
  actorUserId: string | null;
  actorEmail: string | null;
  metadata: Record<string, unknown> | null;
  createdAt: string | null;
}

interface UnseededWorkspaceRow {
  id: string;
  name: string | null;
  slug: string | null;
}

interface HoneytokensResponse {
  summary: HoneytokensSummary;
  leads: HoneytokenLeadRow[];
  clients: HoneytokenClientRow[];
  recentEvents: TouchEventRow[];
  unseededWorkspaces: UnseededWorkspaceRow[];
}

function formatTimeAgo(iso: string | null): string {
  if (!iso) return "—";
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 0) return "just now";
  const sec = Math.floor(ms / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day}d ago`;
  return `${Math.floor(day / 30)}mo ago`;
}

function actionBadgeVariant(action: string): "default" | "secondary" | "destructive" | "outline" {
  if (action === "delete") return "destructive";
  if (action === "update" || action === "activity_create") return "default";
  if (action === "export") return "secondary";
  return "outline";
}

export function HoneytokensContent() {
  const { toast } = useToast();
  const { data, isLoading, refetch, isFetching } = useQuery<HoneytokensResponse>({
    queryKey: ["/api/platform/honeytokens"],
  });

  const seedAllMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/platform/honeytokens/seed", {});
      return await res.json();
    },
    onSuccess: () => {
      toast({ title: "Honeytoken backfill complete", description: "All eligible workspaces have decoy records." });
      queryClient.invalidateQueries({ queryKey: ["/api/platform/honeytokens"] });
    },
    onError: (err: any) => {
      toast({ title: "Backfill failed", description: err?.message || "Unknown error", variant: "destructive" });
    },
  });

  const seedOneMutation = useMutation({
    mutationFn: async (workspaceId: string) => {
      const res = await apiRequest("POST", "/api/platform/honeytokens/seed", { workspaceId });
      return await res.json();
    },
    onSuccess: () => {
      toast({ title: "Workspace seeded", description: "Decoy records planted." });
      queryClient.invalidateQueries({ queryKey: ["/api/platform/honeytokens"] });
    },
    onError: (err: any) => {
      toast({ title: "Seed failed", description: err?.message || "Unknown error", variant: "destructive" });
    },
  });

  return (
    <>
      <div className="p-6 space-y-6" data-testid="page-platform-honeytokens">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold flex items-center gap-2">
              <ShieldAlert className="h-6 w-6" /> Honeytoken Tripwires
            </h1>
            <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
              Decoy lead and client records planted in every workspace. Real users never see them — every
              list, search, and export filters them out. Any read, edit, delete, or export touching one of these
              rows fires a security alert and an audit-log entry below.
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => refetch()}
              disabled={isFetching}
              data-testid="button-refresh-honeytokens"
            >
              {isFetching ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              <span className="ml-2">Refresh</span>
            </Button>
            <Button
              size="sm"
              onClick={() => seedAllMutation.mutate()}
              disabled={seedAllMutation.isPending}
              data-testid="button-seed-all-honeytokens"
            >
              {seedAllMutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Re-run backfill
            </Button>
          </div>
        </div>

        {isLoading ? (
          <div className="text-sm text-muted-foreground">Loading…</div>
        ) : !data ? (
          <div className="text-sm text-muted-foreground">No data.</div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              <SummaryCard label="Workspaces" value={data.summary.totalWorkspaces} testId="stat-total-workspaces" />
              <SummaryCard label="Seeded" value={data.summary.seededWorkspaces} testId="stat-seeded-workspaces" />
              <SummaryCard
                label="Unseeded"
                value={data.summary.unseededWorkspaces}
                tone={data.summary.unseededWorkspaces > 0 ? "warn" : "ok"}
                testId="stat-unseeded-workspaces"
              />
              <SummaryCard label="Lead decoys" value={data.summary.totalLeadHoneytokens} testId="stat-lead-honeytokens" />
              <SummaryCard label="Client decoys" value={data.summary.totalClientHoneytokens} testId="stat-client-honeytokens" />
              <SummaryCard
                label="Touch events"
                value={data.summary.totalTouchEvents}
                tone={data.summary.totalTouchEvents > 0 ? "warn" : "ok"}
                testId="stat-touch-events"
              />
            </div>

            {data.unseededWorkspaces.length > 0 && (
              <Card data-testid="card-unseeded-workspaces">
                <CardHeader>
                  <CardTitle>Unseeded workspaces</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <p className="text-sm text-muted-foreground">
                    These workspaces have no honeytoken rows yet (usually because the workspace was created before
                    a region existed). Click "Seed" to plant decoys now.
                  </p>
                  <ul className="divide-y border rounded">
                    {data.unseededWorkspaces.map((w) => (
                      <li key={w.id} className="flex items-center justify-between p-2 text-sm" data-testid={`row-unseeded-${w.id}`}>
                        <div>
                          <span className="font-medium">{w.name ?? "(unnamed)"}</span>{" "}
                          <span className="text-muted-foreground">{w.slug ? `· ${w.slug}` : ""}</span>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => seedOneMutation.mutate(w.id)}
                          disabled={seedOneMutation.isPending}
                          data-testid={`button-seed-workspace-${w.id}`}
                        >
                          Seed
                        </Button>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            <Card data-testid="card-recent-events">
              <CardHeader>
                <CardTitle>Recent touch events</CardTitle>
              </CardHeader>
              <CardContent>
                {data.recentEvents.length === 0 ? (
                  <div className="text-sm text-muted-foreground">No honeytoken touches recorded.</div>
                ) : (
                  <div className="overflow-auto">
                    <table className="w-full text-sm">
                      <thead className="text-left text-muted-foreground border-b">
                        <tr>
                          <th className="py-2 pr-4">When</th>
                          <th className="py-2 pr-4">Workspace</th>
                          <th className="py-2 pr-4">Type</th>
                          <th className="py-2 pr-4">Action</th>
                          <th className="py-2 pr-4">Record</th>
                          <th className="py-2 pr-4">Actor</th>
                          <th className="py-2 pr-4">IP</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.recentEvents.map((e) => (
                          <tr key={e.id} className="border-b" data-testid={`row-event-${e.id}`}>
                            <td className="py-2 pr-4 whitespace-nowrap">{formatTimeAgo(e.createdAt)}</td>
                            <td className="py-2 pr-4">{e.workspaceName ?? e.workspaceId.slice(0, 8)}</td>
                            <td className="py-2 pr-4">{e.recordType}</td>
                            <td className="py-2 pr-4">
                              <Badge variant={actionBadgeVariant(e.action)} data-testid={`badge-action-${e.id}`}>
                                {e.action}
                              </Badge>
                            </td>
                            <td className="py-2 pr-4">
                              {e.recordName ?? e.recordId.slice(0, 8)}
                            </td>
                            <td className="py-2 pr-4">{e.actorEmail ?? e.actorUserId ?? "—"}</td>
                            <td className="py-2 pr-4 text-xs text-muted-foreground">
                              {(e.metadata as any)?.ipAddress ?? "—"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>

            <div className="grid md:grid-cols-2 gap-4">
              <Card data-testid="card-lead-honeytokens">
                <CardHeader>
                  <CardTitle>Planted lead decoys ({data.leads.length})</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="divide-y text-sm">
                    {data.leads.map((l) => (
                      <li key={l.id} className="py-2" data-testid={`row-lead-${l.id}`}>
                        <div className="font-medium">{l.leadName ?? "(no name)"}</div>
                        <div className="text-xs text-muted-foreground">
                          {l.workspaceName ?? l.workspaceId.slice(0, 8)} · {l.recordId ?? l.id.slice(0, 8)}
                        </div>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
              <Card data-testid="card-client-honeytokens">
                <CardHeader>
                  <CardTitle>Planted client decoys ({data.clients.length})</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="divide-y text-sm">
                    {data.clients.map((c) => (
                      <li key={c.id} className="py-2" data-testid={`row-client-${c.id}`}>
                        <div className="font-medium">{c.accountName ?? "(no name)"}</div>
                        <div className="text-xs text-muted-foreground">
                          {c.workspaceName ?? c.workspaceId.slice(0, 8)} · {c.recordId ?? c.id.slice(0, 8)}
                        </div>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </div>
          </>
        )}

        <p className="text-xs text-muted-foreground">
          See task #594 — honeytokens are planted by <code>server/honeytoken-seeder.ts</code> and touch detection
          lives in <code>server/lib/honeytokens.ts</code>.{" "}
          <Link href="/platform/audit" className="underline">View related audit log →</Link>
        </p>
      </div>
    </>
  );
}

export default function PlatformHoneytokens() {
  return <PlatformLayout><HoneytokensContent /></PlatformLayout>;
}

function SummaryCard({
  label,
  value,
  tone,
  testId,
}: {
  label: string;
  value: number;
  tone?: "ok" | "warn";
  testId: string;
}) {
  return (
    <div
      className={`rounded border p-3 ${tone === "warn" ? "bg-amber-50 dark:bg-amber-950 border-amber-200 dark:border-amber-900" : ""}`}
      data-testid={testId}
    >
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-2xl font-semibold mt-1">{value}</div>
    </div>
  );
}
